import { KeyListRow } from 'components/KeyValueList';
import {
  breakLiesInsideLeg,
  isBreakOnTopForLeg,
  isBreakOnTopInPlanning,
} from 'utils/breakOnTopPlanning';
import {
  hourInMilliseconds,
  minuteInMilliseconds,
  NewAssignmentOverrides,
} from './useCaseAssignmentForms';
import { getLockedFixedTimeInHours } from './fixedTimeInput';
import { BasicFormState, ValidationResults } from 'hooks/useForm';
import Input from 'components/inputs/Input';
import {
  AssigneeWarning,
  AssignmentCancelTypeEnum,
  AssignmentDeviationEnum,
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  BookedToChangeReasonEnum,
  CaseEventType,
  CaseTypeEnum,
  CompanyModel,
  DrivingPolicyReportDto,
  EventModel,
  ExpenseType,
  StandardizedCommentDto,
  StayOverTypeEnum,
  UserModel,
} from 'api/model';
import { arraySpreadIf } from 'utils/spreading';
import Roles, {
  DefaultWebAdminPolicy,
  includesRoles,
  isAuthorized,
  isRiddermarkLockedFromApprovedStatus,
} from 'constants/Roles';
import Select from 'components/inputs/Select';
import {
  BOOKED_TO_CHANGE_REASON_GROUPS,
  bookedToChangeReasonRequired,
  bookedToChangeReasonRequiresComment,
} from 'constants/BookedToChangeReasons';
import styled from 'styled-components';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import AddressInput from 'components/AddressInput';
// import useGetFixedAssignmentTimeFromAddresses from '../CaseAssignments/useGetFixedAssignmentTimeFromAddresses';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useTranslations from 'contexts/basicData/useTranslations';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import TextButton from 'components/inputs/TextButton';
import {
  faClock,
  faExternalLink,
  faSpinner,
  faTruckMoving,
  faUserAlt,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import {
  caseTypeNeverHasFixedTime,
  caseTypeShowsCustomerUpdateMessagesToggle,
  getSelectableCancelTypeOptions,
  getSelectableStatusOptions,
  isFacilityToFacilityLikeCaseType,
  isStaffRentalCaseType,
  statusOptionIsDisabled,
} from '../CaseAssignments/utils';
import Checkbox from 'components/inputs/Checkbox';
import PrioritizedCaseAssignmentUserPicker from 'components/PrioritizedCaseAssignmentUserPicker';
import { CaseDataField, caseDataFormToCaseModel } from './useCaseDataForm';
import { getElectricCarBufferMs } from 'utils/electricCarBuffer';
import EstimatedDurationPicker from '../CaseAssignments/EstimatedDurationPicker';
import RemovableInput from 'components/inputs/RemovableInput';
import {
  useAssignmentCheckHotelNightConflict,
  useAssignmentGetBookedAssignmentsForUserAndDate,
  useAssignmentGetCarTransportTruckAssignmentsForAssignment,
} from 'api/assignment/assignment';
import { makeDateComparator } from 'utils/sorting';
import UserModalLink from 'components/UserModalLink';
import { autoFormatNumberInputOnBlur } from 'utils/input';
import {
  formatDateTime,
  getHoursAndMinutesFromMillisecondsString,
  getStartOfDay,
  removeMilliseconds,
  toInputDateTimeString,
} from 'utils/date-helpers';
import { millisecondsInHour } from 'date-fns';
import TooltipInfoWarning from 'components/TooltipInfoWarning';
import WorkHoursSpan from '../CaseAssignments/WorkHoursSpan';
import StandardizedComments from 'components/StandardizedComments/StandardizedComments';
import AutoApproveCheckDropdown from 'components/AssignmentApproval/AutoApproveCheckDropdown';
import InvoiceExtraDropdown from 'components/AssignmentApproval/InvoiceExtraDropdown';
import useHasAccessToInvoiceExtras from 'hooks/useHasAccessToInvoiceExtras';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import { getUpdatedExpectedExpensesList } from 'utils/expected-expenses';
import { useUserGetDrivingPolicyReportForUnPlannedAssignment } from 'api/user/user';
import {
  maxStandByTimeInHours,
  personalTransportAssignmentTypes,
  userNamesExcemptFromEstimationValidation,
} from 'constants/AppConstants';
import MultiSelect from 'components/inputs/MultiSelect';
import { useDetailedCaseDataForm } from '.';
import useStandardizedComments from 'contexts/standardizedComments/useStandardizedComments';
import { LabelList, LabelTextButton } from '../CaseAssignments/style';
import Routes from 'constants/Routes';
// import useBreakTimeAdjustment from 'hooks/useBreakTimeAdjustment';
import LabelWrap from 'components/inputs/LabelWrap';
import useEstimatedWorkTime from './useEstimatedWorkTime';
import useAssignmentTypeOptions from 'hooks/useAssignmentTypeOptions';
import useStayOverTypeOptions from 'hooks/useStayOverTypeOptions';
import WeeklyHoursStayOverWarning, {
  isStayOverTypeSelected,
} from '../WeeklyHoursStayOverWarning';
import useCarTransportTruckSlots from './useCarTransportTruckSlots';
import useGetAssignmentAddresses from './useGetAssignmentAddresses';
import { openGoogleMapsDirectionsArray } from 'utils/address-helper';
import useHandleChangedAddresses from './useHandleChangedAddresses';
import useEstimatedStopOverTimes from './useEstimatedStopOverTimes';
import useAddPersonalTransportAssignmentRow from './useAddPersonalTransportAssignmentRow';
import { Horizontal, PaddedSpan, WarningDiv } from './style';
import {
  AssignmentField,
  ExcludableKeyListRow,
  isBookedToChanged,
  isSelfTransportationAssignment,
} from './types';
import useAddPlannedStopOverRow from './useAddPlannedStopOverRow';
import useExpenseTypeOptions from './useExpenseTypeOptions';
import RideAlongCarrierPicker from '../CaseAssignments/RideAlongCarrierPicker';
import getFieldTesterPlanningWindow from './fieldTesterPlanningWindow';

/**
 * Parsar standardiserade kommentarer från form-state (lagras som en JSON-sträng). Fältet kan
 * vara tomt (''), saknas (undefined) eller innehålla ogiltig JSON — i samtliga fall vill vi få
 * en tom lista i stället för att JSON.parse kastar och tar ner hela ärendevyn. OBS: både
 * JSON.parse('') och JSON.parse([]) ger "Unexpected end of JSON input", så `?? []` räcker inte.
 */
const parseStandardizedComments = (
  value: string | undefined
): StandardizedCommentDto[] => {
  try {
    return JSON.parse(value || '[]');
  } catch {
    return [];
  }
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ExtraInfoInput = styled(AutoSizedTextArea)`
  resize: vertical;
  width: 395px;
  max-width: 395px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 5px;
`;

interface MinuteAsMillisInputProps {
  label: React.ReactNode;
  assignmentId: number;
  field: AssignmentField;
  value: string;
  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
  canEdit: boolean;
  width?: number;
}

const mkMinuteAsMillisInput = ({
  label,
  assignmentId,
  field,
  value,
  onChange,
  canEdit,
  width = 50,
}: MinuteAsMillisInputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputVal } = e.currentTarget;
    const parsedValue = parseInt(inputVal, 10);
    if (Number.isNaN(parsedValue)) {
      onChange(assignmentId, field, '');
    } else {
      // Multiply minutes by the conversion factor to get milliseconds.
      const valueInMillis = parsedValue * minuteInMilliseconds;
      onChange(assignmentId, field, valueInMillis.toString());
    }
  };

  // Convert the stored millisecond value back to minutes for display.
  const displayValue = value
    ? (Number(value) / minuteInMilliseconds).toString()
    : '';

  return (
    <LabelWrap label={label}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          gap: 5,
        }}
      >
        <Input
          onChange={handleChange}
          small
          width={width}
          type="number"
          value={displayValue}
          disabled={!canEdit}
        />
        <span>min</span>
      </div>
    </LabelWrap>
  );
};

type Props = {
  currentAssignment: AssignmentModel;
  currentCaseType: CaseTypeEnum;
  assignmentId: number;
  state: BasicFormState<AssignmentField>;
  loggedInUser: UserModel | null;
  multiplePurchasesList: AssignmentViewModel[];
  unmodifiedAssignment: AssignmentModel | undefined;
  caseEvents: EventModel[];
  newAssignmentOverrides?: NewAssignmentOverrides;
  resultingStartTime: Date | null;
  resultingEndTime: Date | null;
  validation?: ValidationResults<AssignmentField>;
  carTransportTruckID?: number;

  isViolatingDrivingPolicy: (
    drivingPolicyData: DrivingPolicyReportDto
  ) => boolean;
  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
  onOnHoldClick?: (assignmentId: number) => void;
};

const useCaseAssignmentFormRows = ({
  onChange,
  state,
  assignmentId,
  loggedInUser,
  currentAssignment,
  unmodifiedAssignment,
  currentCaseType,
  multiplePurchasesList,
  caseEvents,
  newAssignmentOverrides,
  resultingEndTime,
  resultingStartTime,
  validation,
  carTransportTruckID,
  isViolatingDrivingPolicy,
  onOnHoldClick,
}: Props) => {
  useCarTransportTruckSlots(assignmentId, state, onChange, carTransportTruckID);

  const { caseResponse } = useDetailedCaseContext();
  const canEditInvoiceExtras = includesRoles(loggedInUser?.roles ?? [], [
    Roles.SuperAdmin,
    Roles.Efterkontroll,
  ]);
  const hasAccessToInvoiceExtras = useHasAccessToInvoiceExtras();

  const loadedByTransportCall =
    useAssignmentGetCarTransportTruckAssignmentsForAssignment(
      {
        assignmentId,
      },
      {
        query: {
          enabled: assignmentId !== -1,
        },
      }
    );

  useEffect(() => {
    // when AssignmentField.assignmentTypeID changes to GoByTrain, set assigneeShouldBookTheirOwnTicket to true, otherwise false
    if (
      Number(state[AssignmentField.assignmentTypeID]) ===
        AssignmentTypeEnum.GoByTrain &&
      currentAssignment.assigneeShouldBookTheirOwnTicket === undefined
    ) {
      onChange(
        assignmentId,
        AssignmentField.assigneeShouldBookTheirOwnTicket,
        'true'
      );
    } else if (
      Number(state[AssignmentField.assignmentTypeID]) !==
        AssignmentTypeEnum.GoByTrain &&
      currentAssignment.assigneeShouldBookTheirOwnTicket === undefined
    ) {
      onChange(
        assignmentId,
        AssignmentField.assigneeShouldBookTheirOwnTicket,
        'false'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state[AssignmentField.assignmentTypeID]]);

  const {
    addresses,
    stopOverToEnd,
    startAddress,
    stopOverAddress,
    endAddress,
  } = useGetAssignmentAddresses(state);

  const { state: caseState } = useDetailedCaseDataForm();

  // Elbilsärenden får aldrig fast tid från prislistan, oavsett rutt: laddstoppen gör tidsåtgången
  // ruttberoende, så TL sätter en estimerad tid precis som på uppdrag utan fast tid. Samma regel
  // för samåkningar, som planeras på estimerad tid (CaseTypeEnum.FixedTimeOnlyByHand i backend).
  // Bara Efterkontroll och SuperAdmin får lägga in en fast tid för hand.
  const isElectricCase = caseState[CaseDataField.isElectric] === 'true';
  const isSamakning = currentCaseType === CaseTypeEnum.Samakning;
  const canSetFixedTimeByHand = includesRoles(loggedInUser?.roles ?? [], [
    Roles.Efterkontroll,
    Roles.SuperAdmin,
  ]);

  // Persontransportben (tåg, buss, flyg …): fast tid gäller bara ben där bilen faktiskt körs.
  // Läs typen ur formulärets state — den kan bytas i samma redigering.
  const isPersonalTransport = isSelfTransportationAssignment(
    Number(state[AssignmentField.assignmentTypeID]) as AssignmentTypeEnum
  );
  // Däckkörning har aldrig fast tid, inte heller för hand (NeverHasFixedTime i backend).
  const neverFixedTime = caseTypeNeverHasFixedTime(currentCaseType);
  // Låst fält: värdet följer regeln i stället för användaren. Persontransport och Däckkörning har
  // aldrig fast tid, och på elbilsärenden och samåkningar sätts den bara av Efterkontroll och SuperAdmin.
  const fixedTimeLocked =
    isPersonalTransport ||
    neverFixedTime ||
    ((isElectricCase || isSamakning) && !canSetFixedTimeByHand);

  // Samåkning med vald bärare: sträckan följer bärarens uppdrag, så från/till låses (backend
  // hämtar dem från bäraren vid spar).
  const followsCarrier =
    currentCaseType === CaseTypeEnum.Samakning &&
    !!Number(state[AssignmentField.ridesAlongWithAssignmentID]);

  // Kravet på bärare (validatorn läser ridesAlongRequired): ny samåkning, eller en som ännu inte
  // planerats. Synkas här och inte i väljaren — raden avmonteras när ärendetypen byts bort från
  // Samåkning, och då måste flaggan släppas, annars spärras sparningen av ett fält som inte syns.
  const requiresCarrier =
    currentCaseType === CaseTypeEnum.Samakning &&
    (state[AssignmentField.isNewAssignment] === 'true' ||
      !unmodifiedAssignment?.assignedTo);
  useEffect(() => {
    const required = requiresCarrier ? 'true' : 'false';
    if (state[AssignmentField.ridesAlongRequired] !== required) {
      onChange(assignmentId, AssignmentField.ridesAlongRequired, required);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, requiresCarrier]);

  const fieldTesterPlanningWindow = getFieldTesterPlanningWindow(
    state,
    state[AssignmentField.estimatedStartTime] ?? resultingStartTime?.toString(),
    Math.floor(Number(state[AssignmentField.estimatedDuration] ?? 0))
  );

  const { isCalculatingFixedTime } = useHandleChangedAddresses({
    assignmentId,
    assignmentStatus: currentAssignment.assignmentStatusID,
    startAddress,
    stopOverAddress,
    endAddress,
    addingStopOver: state[AssignmentField.addingStopOver] === 'true',
    isElectric: isElectricCase,
    noPriceListFixedTime: isPersonalTransport || isSamakning || neverFixedTime,
    onChange,
  });

  const fixedTimeInHours = state[AssignmentField.fixedAssignmentTimeInHours];
  const stopOverFixedTimeInHours =
    state[AssignmentField.stopOverFixedAssignmentTimeInHours];

  // Värdet backend sparar när fältet är låst (CaseLogic.UpdateCaseAssignment).
  const caseWasElectric = caseResponse.response?.case.isElectric === true;
  const caseWasSamakning =
    caseResponse.response?.case.caseTypeID === CaseTypeEnum.Samakning;
  const lockedFixedTimeInHours = getLockedFixedTimeInHours(
    state,
    unmodifiedAssignment,
    {
      // Däckkörning: inget liggande värde står kvar, precis som för persontransport.
      isPersonalTransport: isPersonalTransport || neverFixedTime,
      ruleAppliedBefore: isSamakning ? caseWasSamakning : caseWasElectric,
    }
  );

  useEffect(() => {
    // Låst fält: sätt det värde backend kommer att spara. Rensas en liggande fast tid (ärendet är
    // eller görs till elbil eller samåkning, benet är eller görs till persontransport) blir estimerad uppdragstid
    // obligatorisk igen. Godkända uppdrag rörs inte — där är den fasta tiden redan underlag för
    // lön/pris.
    if (
      !fixedTimeLocked ||
      currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved
    ) {
      return;
    }
    if (fixedTimeInHours !== lockedFixedTimeInHours) {
      onChange(
        assignmentId,
        AssignmentField.fixedAssignmentTimeInHours,
        lockedFixedTimeInHours
      );
    }
    if (stopOverFixedTimeInHours !== '') {
      onChange(
        assignmentId,
        AssignmentField.stopOverFixedAssignmentTimeInHours,
        ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fixedTimeLocked,
    lockedFixedTimeInHours,
    fixedTimeInHours,
    stopOverFixedTimeInHours,
    // Status är redigerbar i formuläret: backas ett godkänt uppdrag till en tidigare
    // status ska en kvarlevande fast tid rensas direkt, inte först vid nästa spar.
    currentAssignment.assignmentStatusID,
  ]);

  // Efterkontroll/SuperAdmin: görs ärendet till elbil eller samåkning i den här redigeringen kommer
  // en liggande fast tid från prislistan, som inte gäller där — rensa den en gång. Backend sparar
  // deras värde som det är, så en fast tid de vill ha skrivs in igen för hand. Utgå från det sparade
  // ärendet också, så att ett elbilsärende eller en samåkning vars formulär-state fylls i efter
  // raden inte ser ut att just ha bytt — då skulle deras eget värde rensas när formuläret öppnas.
  const priceListNeverApplies = isElectricCase || isSamakning;
  const priceListNeverAppliedRef = useRef(
    priceListNeverApplies || caseWasElectric || caseWasSamakning
  );
  useEffect(() => {
    const becameRuleCase =
      priceListNeverApplies && !priceListNeverAppliedRef.current;
    priceListNeverAppliedRef.current = priceListNeverApplies;
    if (
      !becameRuleCase ||
      fixedTimeLocked ||
      currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved
    ) {
      return;
    }
    if (fixedTimeInHours !== '') {
      onChange(assignmentId, AssignmentField.fixedAssignmentTimeInHours, '');
    }
    if (stopOverFixedTimeInHours !== '') {
      onChange(
        assignmentId,
        AssignmentField.stopOverFixedAssignmentTimeInHours,
        ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceListNeverApplies]);

  const { esimatedFinalEndTime } = useEstimatedStopOverTimes(state);
  const isExternalAdmin = includesRoles(loggedInUser?.roles ?? [], [
    Roles.ExternalAdmin,
  ]);

  const { estimatedWorkTimeMillis } = useEstimatedWorkTime(currentAssignment);

  const isAssignedToCarTransport = useMemo(() => {
    return (loadedByTransportCall.data?.length ?? 0) > 0;
  }, [loadedByTransportCall.data?.length]);

  const canEdit = useMemo(() => {
    // return false if the user is an external admin and the assignment has already started
    const now = new Date();
    if (isAssignedToCarTransport) {
      // return false unless the current user's role is EfterKontroll
      return includesRoles(loggedInUser?.roles ?? [], [Roles.Efterkontroll]);
    }
    if (
      isExternalAdmin &&
      unmodifiedAssignment?.startTime &&
      new Date(unmodifiedAssignment.startTime) < now
    ) {
      return false;
    }
    return true;
  }, [
    isAssignedToCarTransport,
    isExternalAdmin,
    loggedInUser?.roles,
    unmodifiedAssignment?.startTime,
  ]);

  const savedCaseType = caseResponse.response?.case.caseTypeID;

  const canEditField = useCallback(
    (field: AssignmentField) => {
      if (!canEdit) {
        return false;
      }

      // Före TestDrive-grenen och mot det sparade ärendets typ, som backend: att byta typ i
      // formuläret ska varken låsa upp eller låsa fältet.
      if (
        field === AssignmentField.assignmentStatusID &&
        isRiddermarkLockedFromApprovedStatus(
          loggedInUser?.roles,
          unmodifiedAssignment?.assignmentStatusID,
          savedCaseType
        )
      ) {
        return false;
      }

      if (currentCaseType === CaseTypeEnum.TestDrive) {
        // specific treatment for TestDrives (to enable Riddermark to edit regardless if also ExternalAdmin)
        const allowedFields = [
          AssignmentField.bookedTo,
          AssignmentField.assignmentStatusID,
          AssignmentField.fromAddress,
          AssignmentField.fromCity,
          AssignmentField.fromCounty,
          AssignmentField.fromName,
          AssignmentField.fromZip,
          AssignmentField.toAddress,
          AssignmentField.toCity,
          AssignmentField.toCounty,
          AssignmentField.toName,
          AssignmentField.toZip,
          AssignmentField.assignedTo,
          AssignmentField.estimatedDuration,
          AssignmentField.estimatedStartTime,
          AssignmentField.startTime,
          AssignmentField.endTime,
          AssignmentField.fixedAssignmentTimeInHours,
          AssignmentField.breakTime,
          AssignmentField.expectedExpenses,
          AssignmentField.standardizedComments,
        ];

        return allowedFields.includes(field);
      }

      // external admins can only edit certain fields
      if (!isExternalAdmin) {
        return true;
      }

      // external admins can only edit not yet approved assignments
      if (
        currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved
      ) {
        return false;
      }

      // specific treatment for external admins
      const allowedFields = [
        AssignmentField.fromAddress,
        AssignmentField.fromCity,
        AssignmentField.fromCounty,
        AssignmentField.fromName,
        AssignmentField.fromZip,
        AssignmentField.toAddress,
        AssignmentField.toCity,
        AssignmentField.toCounty,
        AssignmentField.toName,
        AssignmentField.toZip,
        AssignmentField.bookedTo,
      ];

      // Camboza will be set on RentedStaff (previously Extra) and Samåkning
      if (isStaffRentalCaseType(currentCaseType)) {
        allowedFields.push(
          AssignmentField.assignedTo,
          AssignmentField.estimatedStartTime,
          AssignmentField.estimatedDuration
        );
      }

      return allowedFields.includes(field);
    },
    [
      canEdit,
      currentCaseType,
      currentAssignment.assignmentStatusID,
      isExternalAdmin,
      loggedInUser?.roles,
      unmodifiedAssignment?.assignmentStatusID,
      savedCaseType,
    ]
  );

  const globalStandardizedComments = useStandardizedComments();

  const getBookedAssignmentsForUserAndDate =
    useAssignmentGetBookedAssignmentsForUserAndDate();

  const derivedDuration = useMemo(() => {
    if (currentAssignment.startTime && currentAssignment.endTime) {
      return (
        new Date(currentAssignment.endTime).getTime() -
        new Date(currentAssignment.startTime).getTime()
      );
    }

    // Ej påbörjat uppdrag: laddbufferten är belagd tid och ska in i det fönster
    // körtidspolicykollen prövar (faktisk tid ovan påverkas inte av den).
    return (
      (currentAssignment.estimatedDuration ?? 0) +
      (currentAssignment.electricCarBufferTime ?? 0)
    );
  }, [currentAssignment]);

  const drivingPolicyReportResult =
    useUserGetDrivingPolicyReportForUnPlannedAssignment(
      {
        userId: currentAssignment.assignedTo?.userID,
        bookedTo: currentAssignment.bookedTo,
        startOrEstimatedStartTime:
          currentAssignment.startTime ?? currentAssignment.estimatedStartTime,
        estimatedDuration: derivedDuration,
        endOrEstimatedEndTime: currentAssignment.endTime,
        excludeAssignmentId: currentAssignment.assignmentID,
      },
      {
        query: {
          enabled:
            !!currentAssignment.assignedTo &&
            !userNamesExcemptFromEstimationValidation.includes(
              currentAssignment.assignedTo.name
            ),
        },
      }
    );

  // Hotellspärren (max EN hotellnatt per dag och fälttestare, se HotelStayOverValidator i
  // backend) prövas alltid vid sparning — den här förhandsfrågan finns bara för att gråa ut
  // hotell-alternativet i övernattningsdropdownen och förklara varför INNAN sparförsöket.
  // Uppdrag som startat är undantagna från spärren, därav status-villkoret.
  const hotelNightConflictResult = useAssignmentCheckHotelNightConflict(
    {
      userId: Number(state[AssignmentField.assignedTo]),
      bookedTo:
        state[AssignmentField.bookedTo] !== ''
          ? new Date(state[AssignmentField.bookedTo])
          : undefined,
      excludeAssignmentId: assignmentId >= 0 ? assignmentId : undefined,
    },
    {
      query: {
        enabled:
          state[AssignmentField.assignedTo] !== '' &&
          state[AssignmentField.bookedTo] !== '' &&
          Number(state[AssignmentField.assignmentStatusID]) <
            AssignmentStatusEnum.Started,
      },
    }
  );
  const hotelNightConflictMessage =
    hotelNightConflictResult.data?.conflictMessage;

  // Spegling av backendens "oförändrad hotellnatt"-undantag: en redan sparad hotellnatt för samma
  // FT och dag (inkl. gamla dubbletter) får behållas, så då ska alternativet inte gråas ut.
  const persistedBooksSameHotelNight =
    unmodifiedAssignment?.stayOverTypeID === StayOverTypeEnum.HOTEL &&
    unmodifiedAssignment.assignedTo?.userID ===
      Number(state[AssignmentField.assignedTo]) &&
    !!unmodifiedAssignment.bookedTo &&
    state[AssignmentField.bookedTo] !== '' &&
    getStartOfDay(new Date(unmodifiedAssignment.bookedTo)).getTime() ===
      getStartOfDay(new Date(state[AssignmentField.bookedTo])).getTime();

  const hotelOptionDisabled =
    !!hotelNightConflictMessage && !persistedBooksSameHotelNight;

  const {
    assignmentStatuses,
    assignmentTypes,
    cancelTypes,
    loadingStatuses,
    zipCodeAreas,
  } = useTranslations();

  const assignmentTypeOptions = useAssignmentTypeOptions({ currentCaseType });
  const stayoverTypeOptions = useStayOverTypeOptions();

  const input = useCallback(
    (
      field: AssignmentField
    ): React.FormEventHandler<HTMLInputElement | HTMLSelectElement> =>
      (eve) =>
        onChange(assignmentId, field, eve.currentTarget.value),
    [onChange, assignmentId]
  );

  const mkInput = useCallback(
    (
      name: AssignmentField,
      label: string,
      type?: React.HTMLInputTypeAttribute,
      exclude?: () => boolean
    ): ExcludableKeyListRow => ({
      exclude,
      key: label,
      value: (
        <Input
          onChange={input(name)}
          small
          type={type}
          value={state[name]}
          disabled={!canEditField(name)}
        />
      ),
    }),
    [canEditField, input, state]
  );

  // Har Ride skickat en ny bokningstid som ännu inte hanterats, och är det exakt den tiden som
  // läggs in, är orsaken nästan säkert Ride. Den förväljs då men går att byta. Bara flaggan
  // newDataFromRide räcker inte: den sätts också av adress- och klassningsändringar.
  const rideProposedBookedTimes = useMemo(() => {
    if (!caseResponse.response?.case.newDataFromRide) return new Set<string>();
    return new Set(
      caseEvents
        .filter((e) => e.caseEventType === CaseEventType.RideChange)
        .map((e) =>
          /^Ny bokningstid: (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/.exec(e.message)
        )
        .filter((m): m is RegExpExecArray => m !== null)
        .map((m) => `${m[1]}T${m[2]}`)
    );
  }, [caseEvents, caseResponse.response?.case.newDataFromRide]);
  // Orsaken frågas bara efter på huvuduppdraget, se bookedToChangeReasonRequired.
  const bookedToChanged =
    isBookedToChanged(state) &&
    bookedToChangeReasonRequired(state[AssignmentField.assignmentTypeID]);
  const bookedToChangeReason = state[AssignmentField.bookedToChangeReason];
  const isRideProposedTime =
    bookedToChanged &&
    rideProposedBookedTimes.has(state[AssignmentField.bookedTo]);
  // Om förvalet gjordes här och tiden sedan ändras bort från Ride-tiden ska det tas bort igen,
  // annars står Ride kvar som orsak till en flytt Ride inte bad om.
  const rideReasonPrefilled = useRef(false);
  useEffect(() => {
    const rideReason = String(BookedToChangeReasonEnum.ChangedInRide);
    if (isRideProposedTime && bookedToChangeReason === '') {
      rideReasonPrefilled.current = true;
      onChange(assignmentId, AssignmentField.bookedToChangeReason, rideReason);
    } else if (
      !isRideProposedTime &&
      rideReasonPrefilled.current &&
      bookedToChangeReason === rideReason
    ) {
      rideReasonPrefilled.current = false;
      onChange(assignmentId, AssignmentField.bookedToChangeReason, '');
    }
  }, [assignmentId, bookedToChangeReason, isRideProposedTime, onChange]);

  const clearEstimatedDuration = useCallback(() => {
    // onChange(assignmentId, AssignmentField.estimatedDuration, '0');
    onChange(assignmentId, AssignmentField.systemEstimatedDurationExact, '0');
    onChange(assignmentId, AssignmentField.systemEstimatedDurationApprox, '0');
  }, [onChange, assignmentId]);

  const mkAddressInput = useCallback(
    (
      name: AssignmentField,
      address: AssignmentField,
      city: AssignmentField,
      zip: AssignmentField,
      county: AssignmentField,
      label: ReactNode,
      // handleCompanyPicked: (company: CompanyModel, newCounty?: ZipCodeArea) => void,
      filter?: (c: CompanyModel) => boolean,
      disabled?: boolean,
      warning?: string,
      extra?: React.ReactNode,
      // When set, every change to this address group is mirrored onto the given
      // fields. Used for "Uthyrd personal" (RentedStaff) where the single
      // "Arbetsplats" input drives both from- and to-address (to == from).
      mirrorToFields?: {
        name: AssignmentField;
        address: AssignmentField;
        city: AssignmentField;
        zip: AssignmentField;
        county: AssignmentField;
      }
    ): ExcludableKeyListRow => {
      const fromToMirrorMap: Partial<Record<AssignmentField, AssignmentField>> =
        mirrorToFields
          ? {
              [name]: mirrorToFields.name,
              [address]: mirrorToFields.address,
              [city]: mirrorToFields.city,
              [zip]: mirrorToFields.zip,
              [county]: mirrorToFields.county,
            }
          : {};

      const companyFieldChanged = (
        fieldName: AssignmentField,
        value: string
      ) => {
        onChange(assignmentId, fieldName, value);
        const mirrorField = fromToMirrorMap[fieldName];
        if (mirrorField) {
          onChange(assignmentId, mirrorField, value);
        }

        // const companyTransportPrice = getFixedAssignmentTimeFromAddresses({
        //   ...state,
        //   [fieldName]: value,
        // });

        // onChange(
        //   assignmentId,
        //   AssignmentField.fixedAssignmentTimeInHours,
        //   companyTransportPrice
        //     ? (
        //         companyTransportPrice.fixedTimeInMillseconds /
        //         hourInMilliseconds
        //       ).toString()
        //     : ''
        // );

        // if the fixed assignment exceeds 5h, set the break time to 30 minutes per 5h
        // adjustBreakTimeIfNecessary(companyTransportPrice);
      };

      return {
        field: name,
        key: label,
        id: name,
        value: (
          <Row>
            <AddressInput
              address={state[address]}
              city={state[city]}
              companyOptionFilter={filter}
              countyId={state[county]}
              name={state[name]}
              onAddressChanged={(v) => companyFieldChanged(address, v)}
              onCityChanged={(v) => companyFieldChanged(city, v)}
              onCompanyPicked={(company, newCounty) => {
                onChange(assignmentId, address, company.address);
                onChange(assignmentId, name, company.name);
                onChange(assignmentId, city, company.city);
                onChange(assignmentId, county, newCounty?.id.toString() ?? '');
                onChange(assignmentId, zip, company.zip);
                if (mirrorToFields) {
                  onChange(
                    assignmentId,
                    mirrorToFields.address,
                    company.address
                  );
                  onChange(assignmentId, mirrorToFields.name, company.name);
                  onChange(assignmentId, mirrorToFields.city, company.city);
                  onChange(
                    assignmentId,
                    mirrorToFields.county,
                    newCounty?.id.toString() ?? ''
                  );
                  onChange(assignmentId, mirrorToFields.zip, company.zip);
                }
                clearEstimatedDuration();
                // handleCompanyPicked(company, newCounty);
              }}
              onCountyChanged={(v) => {
                // County field is not used to look up CompanyTransportPrices
                onChange(assignmentId, county, v?.id.toString() ?? '');
                if (mirrorToFields) {
                  onChange(
                    assignmentId,
                    mirrorToFields.county,
                    v?.id.toString() ?? ''
                  );
                }
              }}
              onNameChanged={(v) => companyFieldChanged(name, v)}
              onZipChanged={(v) => companyFieldChanged(zip, v)}
              zip={state[zip]}
              disabled={disabled}
            />
            {warning && (
              <WarningDiv>
                {warning}
                <FontAwesomeIcon icon={faWarning} />
              </WarningDiv>
            )}
            {extra}
          </Row>
        ),
      };
    },
    [state, onChange, assignmentId, clearEstimatedDuration]
  );

  const getNextStartedAssignmentTime = useCallback(async (): Promise<
    Date | undefined
  > => {
    // first, get all assignments for the user for the same day
    const assignmentsForDate =
      await getBookedAssignmentsForUserAndDate.mutateAsync({
        params: {
          userId: Number(state[AssignmentField.assignedTo]),
          date: new Date(state[AssignmentField.estimatedStartTime]),
        },
      });

    // next, find the next assignment that has started
    const nextStartedAssignment = assignmentsForDate?.assignments
      .filter((a) => a.assignment.startTime)
      .sort(makeDateComparator((a) => a.assignment.startTime!))
      .find(
        (a) =>
          a.assignment.startTime &&
          a.assignment.startTime >
            new Date(state[AssignmentField.estimatedStartTime])
      );
    return nextStartedAssignment?.assignment.startTime;
  }, [getBookedAssignmentsForUserAndDate, state]);

  const expenseTypeOptions = useExpenseTypeOptions();

  const {
    fromAssignment: personalTransportFromAssignmentRow,
    toAssignment: personalTransportToAssignmentRow,
  } = useAddPersonalTransportAssignmentRow({
    assignmentId,
    currentCaseType,
    state,
    validation,

    mkAddressInputFn: mkAddressInput,
    onChange,
  });

  const addStopOverRow = useAddPlannedStopOverRow({
    state,
    assignmentId,
    currentCaseType,
    canEdit,

    onChange,
    mkAddressInputFn: mkAddressInput,
  });

  const rows: KeyListRow[] = useMemo(() => {
    const result: (KeyListRow & {
      exclude?: () => boolean;
    })[] = [
      {
        exclude: () => !isAssignedToCarTransport,
        key: 'Tilldelad till lastbil',
        value: (
          <LabelList>
            {currentAssignment.loadedByTransport.map((t) => (
              <LabelTextButton
                key={t.carTransportTruckAssignmentID}
                onClick={() =>
                  window.open(
                    `${Routes.internalDeliveryGroup.carTransportTrucks.index}/${t.carTransportTruckCaseID}`,
                    t.carTransportTruckAssignmentID.toString() ?? '_blank'
                  )
                }
                title={`Tilldelad till lastbil ${t.carTransportTruckRegistrationNumber}`}
              >
                <span>{t.carTransportTruckRegistrationNumber}</span>
                <FontAwesomeIcon icon={faTruckMoving} />
                <span>{loadingStatuses[t.loadingStatus]}</span>
              </LabelTextButton>
            ))}
          </LabelList>
        ),
      },
      mkInput(AssignmentField.bookedTo, 'Bokad till', 'datetime-local'),
      ...arraySpreadIf(bookedToChanged, {
        key: 'Orsak till ny tid',
        value: (
          <Select
            onChange={input(AssignmentField.bookedToChangeReason)}
            small
            error={!!validation?.[AssignmentField.bookedToChangeReason]}
            disabled={!canEditField(AssignmentField.bookedTo)}
            value={bookedToChangeReason}
          >
            <option value="">Välj orsak</option>
            {BOOKED_TO_CHANGE_REASON_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.reasons.map((reason) => (
                  <option key={reason.value} value={String(reason.value)}>
                    {reason.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        ),
      }),
      ...arraySpreadIf(bookedToChanged, {
        key: 'Kommentar till ny tid',
        value: (
          <ExtraInfoInput
            disabled={!canEditField(AssignmentField.bookedTo)}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.bookedToChangeComment,
                eve.currentTarget.value
              )
            }
            placeholder={
              bookedToChangeReasonRequiresComment(bookedToChangeReason)
                ? 'Krävs när orsaken är Annat'
                : 'Valfri, t.ex. "Kunden jobbar till 16"'
            }
            value={state[AssignmentField.bookedToChangeComment]}
          />
        ),
      }),
      {
        exclude: () =>
          currentCaseType === CaseTypeEnum.Placeholder ||
          !(
            currentAssignment.assignmentStatusID ===
              AssignmentStatusEnum.Approved &&
            currentAssignment.assignmentDeviationID ===
              AssignmentDeviationEnum.None &&
            !!loggedInUser?.roles.includes(Roles.GoSupport)
          ),
        key: 'Fälttestarutvärdering',
        value: (
          <Select
            onChange={input(AssignmentField.assignedToRating)}
            small
            value={state[AssignmentField.assignedToRating]}
          >
            {
              // undefined option
            }
            <option value={undefined}>Välj ett betyg</option>
            {
              // 1-5
              Array.from(Array(5).keys()).map((i) => (
                <option key={i} value={i + 1}>
                  {i + 1}
                </option>
              ))
            }
          </Select>
        ),
      },

      {
        exclude: () =>
          !(
            currentAssignment.assignmentStatusID ===
              AssignmentStatusEnum.Approved &&
            currentAssignment.assignmentDeviationID ===
              AssignmentDeviationEnum.None &&
            !!loggedInUser?.roles.includes(Roles.GoSupport)
          ),
        key: 'Fälttestarutvärdering anledning',
        value: (
          <ExtraInfoInput
            // disabled
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.assignedToRatingReason,
                eve.currentTarget.value
              )
            }
            value={state[AssignmentField.assignedToRatingReason]}
          />
        ),
      },
      {
        key: 'Typ',
        value: (
          <Select
            disabled={!canEditField(AssignmentField.assignmentTypeID)}
            onChange={(eve) => {
              input(AssignmentField.assignmentTypeID)(eve);
            }}
            small
            value={state[AssignmentField.assignmentTypeID]}
            width="auto"
          >
            {assignmentTypeOptions.map((assignmentType) => (
              <option key={assignmentType} value={assignmentType}>
                {assignmentTypes[assignmentType]}
              </option>
            ))}
          </Select>
        ),
      },
      {
        exclude: () => multiplePurchasesList.length === 0,
        key: 'Flerbilsinköp',
        value: (
          <>
            {multiplePurchasesList.map((ass) => (
              <TextButton
                key={ass.assignment.assignmentID}
                onClick={() =>
                  window.open(
                    `/sok/${ass.case.caseID}`,
                    ass.case.caseID.toString() ?? '_blank'
                  )
                }
                title={`Flerbilsinköp med ${ass.case.registrationNumber}`}
              >
                {ass.case.registrationNumber}{' '}
                {!!ass.assignment.estimatedDuration &&
                  ass.assignment.assignedTo?.userID ===
                    Number(state[AssignmentField.assignedTo]) && (
                    <FontAwesomeIcon
                      title={`${ass.assignment.assignedTo?.name} grunduppdrag`}
                      icon={faClock}
                    />
                  )}
                {ass.assignment.assignedTo?.userID ===
                  Number(state[AssignmentField.assignedTo]) && (
                  <FontAwesomeIcon
                    title={ass.assignment.assignedTo?.name}
                    icon={faUserAlt}
                  />
                )}{' '}
                <FontAwesomeIcon icon={faExternalLink} />
              </TextButton>
            ))}
          </>
        ),
      },
      {
        key: 'Status',
        value: (
          <Select
            disabled={!canEditField(AssignmentField.assignmentStatusID)}
            onChange={(eve) => {
              const newStatus = Number(eve.currentTarget.value);
              if (newStatus === AssignmentStatusEnum.OnHold) {
                onOnHoldClick?.(assignmentId);
                return;
              }
              onChange(
                assignmentId,
                AssignmentField.assignmentStatusID,
                eve.currentTarget.value
              );
            }}
            small
            value={state[AssignmentField.assignmentStatusID]}
          >
            {getSelectableStatusOptions(
              unmodifiedAssignment?.assignmentStatusID ??
                AssignmentStatusEnum.Created,
              currentCaseType,
              unmodifiedAssignment
            ).map((status) => (
              <option
                disabled={statusOptionIsDisabled({
                  currentAssignmentStatus: Number(
                    status
                  ) as AssignmentStatusEnum,
                  events: caseEvents,
                  currentAssignedToId: currentAssignment.assignedTo?.userID,
                  currentAssignmentId: currentAssignment.assignmentID,
                  drivingPolicyAssignmentInNeedOfHandling:
                    currentAssignment.drivingPolicyAssignmentInNeedOfHandling,
                })}
                key={status}
                value={status}
              >
                {assignmentStatuses[status]}
              </option>
            ))}
          </Select>
        ),
      },
      {
        exclude: () =>
          currentAssignment.assignmentTypeID ===
            AssignmentTypeEnum.CarPlaceholder ||
          currentCaseType === CaseTypeEnum.CarTransportTruck,
        key: 'Tillåt privat mellanlandning',
        value: (
          <Checkbox
            disabled={!canEditField(AssignmentField.allowPrivateStopOver)}
            checked={state[AssignmentField.allowPrivateStopOver] === 'true'}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.allowPrivateStopOver,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
      },
      {
        // "Nyckel inlämnad" gäller bara leveranser som förbereds på anläggning
        // (hemleverans/butiksleverans/flytt mellan anläggningar). Nullable: '' = ej angivet.
        exclude: () =>
          currentCaseType !== CaseTypeEnum.HomeDelivery &&
          !isFacilityToFacilityLikeCaseType(currentCaseType),
        key: 'Nyckel inlämnad',
        value: (
          <Select
            disabled={!canEditField(AssignmentField.keyHandedIn)}
            onChange={input(AssignmentField.keyHandedIn)}
            small
            value={state[AssignmentField.keyHandedIn]}
          >
            <option value="">Ej angivet</option>
            <option value="true">Ja</option>
            <option value="false">Nej</option>
          </Select>
        ),
      },
      {
        // "Ska på lastbil": just detta ben ska transporteras på lastbil (inte köras av FT).
        // Per uppdrag så att ett uppdelat ärende kan köras individuellt på ett ben och gå på
        // lastbil på ett annat. Ärendet hamnar i "Ska på lastbil"-listan så länge något ben har
        // bocken; startklar-monitorn visar lastbils-badge bara på det flaggade benet. Nollställs
        // automatiskt när benet lastas på en lastbil. Meningslös på lastbilsuppdraget självt
        // och på bil-platshållare.
        exclude: () =>
          currentAssignment.assignmentTypeID ===
            AssignmentTypeEnum.CarPlaceholder ||
          currentCaseType === CaseTypeEnum.CarTransportTruck,
        key: 'Ska på lastbil',
        value: (
          <Checkbox
            disabled={!canEditField(AssignmentField.shouldGoOnTruck)}
            checked={state[AssignmentField.shouldGoOnTruck] === 'true'}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.shouldGoOnTruck,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
      },
      {
        exclude: () =>
          currentCaseType === CaseTypeEnum.Placeholder ||
          state[AssignmentField.assignmentCancelTypeID] === '',
        key: 'Avbokningstyp',
        value: (
          <Select
            onChange={input(AssignmentField.assignmentCancelTypeID)}
            small
            disabled={!canEditField(AssignmentField.assignmentCancelTypeID)}
            value={state[AssignmentField.assignmentCancelTypeID]}
          >
            {getSelectableCancelTypeOptions(
              currentAssignment,
              currentCaseType,
              caseResponse.response?.linkedCases
            ).map(({ option, disabled }, index) => (
              <option
                disabled={disabled}
                key={`avbokningstyp-${option}-${index}`}
                value={option}
              >
                {option !== undefined ? cancelTypes[option] : 'Ingen vald'}
              </option>
            ))}
          </Select>
        ),
      },
      {
        exclude: () =>
          !caseTypeShowsCustomerUpdateMessagesToggle(currentCaseType),
        key: 'Skicka SMS',
        value: (
          <Checkbox
            disabled={!canEditField(AssignmentField.sendCustomerUpdateMessages)}
            checked={
              currentCaseType !== CaseTypeEnum.Placeholder &&
              state[AssignmentField.sendCustomerUpdateMessages] === 'true'
            }
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.sendCustomerUpdateMessages,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
      },
      {
        // Samåkning: vilket uppdrag (vilken bil) passageraren åker med. Krav när en ny samåkning
        // planeras; valet fyller sträcka, start och fast tid från bäraren (estimerad tid om
        // bäraren är en elbil — elbilar har ingen fast tid).
        exclude: () => currentCaseType !== CaseTypeEnum.Samakning,
        key: 'Samåker med',
        value: (
          <RideAlongCarrierPicker
            assignmentId={assignmentId}
            disabled={!canEdit}
            requiresCarrier={requiresCarrier}
            canSetFixedTimeByHand={canSetFixedTimeByHand}
            onChange={onChange}
            state={state}
          />
        ),
      },
      personalTransportToAssignmentRow,
      mkAddressInput(
        AssignmentField.fromName,
        AssignmentField.fromAddress,
        AssignmentField.fromCity,
        AssignmentField.fromZip,
        AssignmentField.fromCounty,
        currentCaseType === CaseTypeEnum.RentedStaff ? 'Arbetsplats' : 'Från',
        (c) => {
          if (
            currentAssignment.assignmentTypeID ===
            AssignmentTypeEnum.FurtherTransport
          ) {
            // further transports allow all addresses
            return true;
          }
          if (
            personalTransportAssignmentTypes.includes(
              currentAssignment.assignmentTypeID
            )
          ) {
            return true;
          }
          switch (currentCaseType) {
            case CaseTypeEnum.CarTransportTruck:
              // only show all isRiddermark and Zäkra addresses
              return c.isRiddermark || c.isZakraHub === true;
            case CaseTypeEnum.InternalDelivery:
              // only show logistics centers (i.e. Strängnäs) or Zäkra
              return c.isLogisticsCenter === true || c.isZakraHub === true;
            case CaseTypeEnum.InternalDeliverySales:
              // only show logistics center (i.e. Strängnäs)
              return c.isLogisticsCenter === true || c.isZakraHub === true;
            case CaseTypeEnum.FacilityToFacility:
            case CaseTypeEnum.FacilityTransfer:
              // only show non-logistic isRiddermark addresses
              return c.isRiddermark || c.isZakraHub === true;
            case CaseTypeEnum.Verkstadsbilar:
              // Verkstadsbilar täcker både ut- och returkörningar (fd två separata
              // ärendetyper), så uppdraget kan starta på logistikcenter eller extern verkstad
              return (
                c.isExternalWorkshop === true ||
                c.isLogisticsCenter === true ||
                c.name.toLowerCase() === 'zäkra'
              );
            default:
              return true;
          }
        },
        !canEdit || followsCarrier,
        // Varna bara på dagens första uppdrag (morgonutfyllnad): då börjar
        // fälttestaren hemifrån och en från-adress utanför hemområdet är misstänkt.
        state[AssignmentField.fromAddressIsOutSideHomeArea] === 'true' &&
          currentAssignment.assignmentID === -1 &&
          state[AssignmentField.isUsersFirstAssignmentOfDay] === 'true'
          ? 'Adressen ligger utanför fälttestarens hemområde'
          : '',
        undefined,
        // Uthyrd personal: spegla arbetsplatsen till till-adressen (to == from).
        currentCaseType === CaseTypeEnum.RentedStaff
          ? {
              name: AssignmentField.toName,
              address: AssignmentField.toAddress,
              city: AssignmentField.toCity,
              zip: AssignmentField.toZip,
              county: AssignmentField.toCounty,
            }
          : undefined
      ),

      addStopOverRow,
      {
        ...mkAddressInput(
          AssignmentField.toName,
          AssignmentField.toAddress,
          AssignmentField.toCity,
          AssignmentField.toZip,
          AssignmentField.toCounty,
          'Till',
          (c) => {
            if (
              personalTransportAssignmentTypes.includes(
                currentAssignment.assignmentTypeID
              )
            ) {
              return true;
            }
            switch (currentCaseType) {
              case CaseTypeEnum.CarTransportTruck:
                // only show all isRiddermark and Zäkra addresses
                return c.isRiddermark || c.isZakraHub === true;
              case CaseTypeEnum.InternalDelivery:
              case CaseTypeEnum.InternalDeliverySales:
                // only riddermark addresses, internal overview companies, and Zäkra's address
                return (
                  c.showInInternalDeliveryOverview === true ||
                  c.isRiddermark === true ||
                  c.isLogisticsCenter === true ||
                  c.isZakraHub === true
                );
              case CaseTypeEnum.FacilityToFacility:
              case CaseTypeEnum.FacilityTransfer:
                // only show non-logistic isRiddermark addresses
                return (
                  (!c.isLogisticsCenter && c.isRiddermark) ||
                  c.isZakraHub === true
                );
              case CaseTypeEnum.Verkstadsbilar:
                // Verkstadsbilar täcker både ut- och returkörningar (fd två separata
                // ärendetyper), så uppdraget kan sluta på extern verkstad eller logistikcenter
                return (
                  c.isExternalWorkshop === true ||
                  c.isLogisticsCenter === true ||
                  c.name.toLowerCase() === 'zäkra'
                );
              default:
                return true;
            }
          },
          !canEdit || followsCarrier,
          undefined,
          state[AssignmentField.addingStopOver] === 'true' && (
            <TextButton
              onClick={() => {
                openGoogleMapsDirectionsArray(stopOverToEnd);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Öppna i Google Maps"
            >
              <img
                alt="Google maps icon"
                height={15}
                src="/images/googlemapsiconsmall.png"
                width={15}
              />
            </TextButton>
          )
        ),
        // Uthyrd personal: till-adressen är dold; den speglas automatiskt från
        // arbetsplatsen (from == to).
        exclude: () => currentCaseType === CaseTypeEnum.RentedStaff,
      },
      personalTransportFromAssignmentRow,
      {
        key: 'Fälttestare',
        value: (
          <Centered>
            {isAssignedToCarTransport ? (
              'Fälttestare kan ej tilldelas bilar som har lastbilstransport'
            ) : (
              <PrioritizedCaseAssignmentUserPicker
                actualEndTime={state[AssignmentField.endTime]}
                actualStartTime={state[AssignmentField.startTime]}
                assignmentId={assignmentId}
                assignmentType={
                  Number(
                    state[AssignmentField.assignmentTypeID]
                  ) as AssignmentTypeEnum
                }
                bookedTo={state[AssignmentField.bookedTo]}
                caseModel={caseDataFormToCaseModel(
                  undefined,
                  caseState,
                  zipCodeAreas
                )}
                disabled={
                  !canEditField(AssignmentField.assignedTo) ||
                  isAssignedToCarTransport
                }
                // Körtidspolicyn bedöms mot huvudbenet PLUS de inlagda persontransportbenen som
                // fälttestaren själv åker — se getFieldTesterPlanningWindow.
                estimatedDuration={
                  fieldTesterPlanningWindow.estimatedDurationMs
                }
                estimatedStartTime={
                  fieldTesterPlanningWindow.estimatedStartTime
                }
                fromZipCode={state[AssignmentField.fromZip]}
                // Fälttestaren kan väljas redan när uppdraget skapas. Spärren (2024-05) fanns för att
                // nya uppdrag då inte registrerades i körtidspolicyn vid skapande; det gör de sedan
                // 7eac6fb296 (CreateCase → AddInitialAssignment). Väljarens lista räknar körtids-
                // policyn på de planerade tiderna även utan uppdrags-id, så ovalbara FT kan inte väljas.
                requireExistingAssignmentToSelectFT={false}
                allowRunnerOverride
                onUserPicked={async (
                  user,
                  _breaksDrivingPolicy,
                  runnerOverrideReason
                ) => {
                  onChange(
                    assignmentId,
                    AssignmentField.assignedTo,
                    user?.userID.toString() ?? ''
                  );
                  // Motiveringen hör till just det här valet — ett nytt val nollar den.
                  onChange(
                    assignmentId,
                    AssignmentField.runnerCustomerLegOverrideReason,
                    runnerOverrideReason ?? ''
                  );

                  const currentStatus = Number(
                    state[AssignmentField.assignmentStatusID]
                  );

                  // Deselcting user changes status to Created
                  if (
                    !user &&
                    (currentStatus === AssignmentStatusEnum.Planned ||
                      currentStatus === AssignmentStatusEnum.Assigned)
                  ) {
                    onChange(
                      assignmentId,
                      AssignmentField.assignmentStatusID,
                      AssignmentStatusEnum.Created.toString()
                    );
                  }

                  // Selecting user changes status to Planned
                  if (
                    user &&
                    (currentStatus === AssignmentStatusEnum.Created ||
                      currentStatus === AssignmentStatusEnum.Assigned)
                  ) {
                    onChange(
                      assignmentId,
                      AssignmentField.assignmentStatusID,
                      AssignmentStatusEnum.Planned.toString()
                    );
                  }
                }}
                selectedUserId={
                  state[AssignmentField.assignedTo] === ''
                    ? null
                    : Number(state[AssignmentField.assignedTo])
                }
                estimatedLoadingTime={
                  state[AssignmentField.estimatedLoadingTime] !== ''
                    ? Number(state[AssignmentField.estimatedLoadingTime])
                    : undefined
                }
                estimatedUnloadingTime={
                  state[AssignmentField.estimatedUnloadingTime] !== ''
                    ? Number(state[AssignmentField.estimatedUnloadingTime])
                    : undefined
                }
                electricCarBufferMs={getElectricCarBufferMs(
                  caseState[CaseDataField.isElectric] === 'true',
                  state[AssignmentField.assignmentTypeID]
                    ? Number(state[AssignmentField.assignmentTypeID])
                    : undefined,
                  currentAssignment.fromCounty?.id,
                  currentAssignment.toCounty?.id,
                  // Persisterade koordinater (utelämnade av assignmentFormToAssignment när
                  // adressen ändrats i formuläret) — 10 km-regeln speglar servern.
                  currentAssignment,
                  // Sparat eller TL-valt värde (live-uträknat av assignmentFormToAssignment).
                  currentAssignment.electricCarBufferTime
                )}
              />
            )}

            {state[AssignmentField.assignedTo] !== '' && (
              <PaddedSpan>
                {isAuthorized(
                  DefaultWebAdminPolicy.allow,
                  DefaultWebAdminPolicy.deny,
                  loggedInUser?.roles ?? []
                ) && (
                  <UserModalLink
                    userId={Number(state[AssignmentField.assignedTo])}
                  />
                )}{' '}
                {(drivingPolicyReportResult.data &&
                  isViolatingDrivingPolicy(drivingPolicyReportResult.data)) ||
                  (drivingPolicyReportResult.data?.warnings.includes(
                    AssigneeWarning.CannotStayAtHotels
                  ) && (
                    <TooltipInfoWarning
                      warnings={drivingPolicyReportResult.data?.warnings}
                      showOnlyTooltip
                    />
                  ))}
                {isAuthorized(
                  DefaultWebAdminPolicy.allow,
                  DefaultWebAdminPolicy.deny,
                  loggedInUser?.roles ?? []
                ) &&
                  state[AssignmentField.assignedTo] &&
                  assignmentId >= 0 && (
                    <WorkHoursSpan
                      drivinPolicyReport={drivingPolicyReportResult.data}
                    />
                  )}
              </PaddedSpan>
            )}
          </Centered>
        ),
      },
      {
        key:
          currentCaseType === CaseTypeEnum.CarTransportTruck
            ? 'Estimerad körtid'
            : 'Estimerad uppdragstid',
        value: (
          <Horizontal>
            {/* minWidth (inte width): med "+30 min (elbil)"-etiketten måste
                containern kunna växa, annars radbryts etiketten och raden blir
                dubbelt så hög. */}
            <div
              style={{
                minWidth: 220,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <EstimatedDurationPicker
                // The picker edits the pure DRIVE estimate. The form-state estimatedDuration field already holds
                // the drive (load subtracts break from the stored window; save adds it back), so bind the picker
                // to that field rather than currentAssignment.estimatedDuration which is the stored window.
                assignment={{
                  ...currentAssignment,
                  estimatedDuration:
                    state[AssignmentField.estimatedDuration] === '' ||
                    state[AssignmentField.estimatedDuration] === undefined
                      ? currentAssignment.estimatedDuration
                      : Number(state[AssignmentField.estimatedDuration]),
                }}
                caseType={currentCaseType}
                isElectric={caseState[CaseDataField.isElectric] === 'true'}
                // disable the estimated duration picker if the assignment has a fixed assignment time
                disabled={
                  !canEditField(AssignmentField.estimatedDuration) ||
                  !!state[AssignmentField.fixedAssignmentTimeInHours] ||
                  (currentAssignment.assignmentStatusID >=
                    AssignmentStatusEnum.Complete &&
                    !loggedInUser?.roles.includes(
                      /* Roles.GoSupport */ Roles.Admin
                    )) // temporary allow all admins to change start time (during summer vacation)
                }
                hideSelectAverageCheckbox
                onChangeEstimatedDuration={(value: number) => {
                  const roundedValue = Math.floor(value);
                  onChange(
                    assignmentId,
                    AssignmentField.estimatedDuration,
                    String(roundedValue)
                  );
                }}
                onChangeSystemEstimatedDurationApprox={(value: number) => {
                  onChange(
                    assignmentId,
                    AssignmentField.systemEstimatedDurationApprox,
                    value.toString()
                  );
                }}
                onChangeSystemEstimatedDurationExact={(value: number) => {
                  onChange(
                    assignmentId,
                    AssignmentField.systemEstimatedDurationExact,
                    value.toString()
                  );
                }}
                addresses={addresses}
                onChangeElectricCarBuffer={(bufferMs) => {
                  onChange(
                    assignmentId,
                    AssignmentField.manualElectricCarBufferTime,
                    String(bufferMs)
                  );
                }}
              />
            </div>
            {
              // For regular assignments where break-on-top planning is active, clarify that the estimate is the
              // pure drive time; the break is added on top (it does not belong inside this number).
              !state[AssignmentField.fixedAssignmentTimeInHours] &&
                isBreakOnTopForLeg(
                  currentAssignment.bookedTo,
                  currentAssignment.assignmentTypeID,
                  currentCaseType
                ) && (
                  <PaddedSpan style={{ fontSize: '0.8em', color: '#666' }}>
                    Estimatet ska spegla körtiden exklusive rast – rasten läggs
                    ovanpå den estimerade tiden.
                  </PaddedSpan>
                )
            }
            {
              // Passenger legs: the break is taken during the trip, so it stays inside the estimate.
              isBreakOnTopInPlanning(currentAssignment.bookedTo) &&
                breakLiesInsideLeg(
                  currentAssignment.assignmentTypeID,
                  currentCaseType
                ) && (
                  <PaddedSpan style={{ fontSize: '0.8em', color: '#666' }}>
                    Rasten tas under resan och läggs inte ovanpå den estimerade
                    tiden.
                  </PaddedSpan>
                )
            }
            {
              // show if no other assignment has an estimated duration and start time
              multiplePurchasesList.length > 0 &&
                state[
                  AssignmentField
                    .hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime
                ] !== 'true' && (
                  <PaddedSpan style={{ opacity: 0.4 }}>
                    OBS! Inkludera samtliga inköpsuppdrag i den beräknade
                    totaltiden om du planerar att samma FT ska göra alla inköp.
                  </PaddedSpan>
                )
            }
            {
              // if we are adding a stopover, highlight that the estimated duration should be from the stopover to the end
              state[AssignmentField.addingStopOver] === 'true' && (
                <WarningDiv>
                  OBS: Eftersom du lägger till en mellanlandning ska denna tid
                  vara från startadressen till mellanlandningen och inte den
                  totala tiden.
                </WarningDiv>
              )
            }
          </Horizontal>
        ),
      },
      ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
        key: 'Beräknad tid för',
        value: (
          <Horizontal
            style={{
              gap: 20,
              justifyContent: 'space-between',
              maxWidth: 100,
            }}
          >
            {mkMinuteAsMillisInput({
              label: <span style={{ fontSize: '0.9em' }}>lastning</span>,
              assignmentId: currentAssignment.assignmentID,
              field: AssignmentField.estimatedLoadingTime,
              value: state[AssignmentField.estimatedLoadingTime] || '',
              onChange,
              canEdit: canEditField(AssignmentField.estimatedLoadingTime),
            })}
            {mkMinuteAsMillisInput({
              label: <span style={{ fontSize: '0.9em' }}>lossning</span>,
              assignmentId: currentAssignment.assignmentID,
              field: AssignmentField.estimatedUnloadingTime,
              value: state[AssignmentField.estimatedUnloadingTime] || '',
              onChange,
              canEdit: canEditField(AssignmentField.estimatedUnloadingTime),
            })}
          </Horizontal>
        ),
      }),
      ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
        key: 'Total arbetstid',
        value: getHoursAndMinutesFromMillisecondsString(
          estimatedWorkTimeMillis
        ),
      }),
      ...arraySpreadIf(
        currentCaseType !== CaseTypeEnum.Placeholder &&
          (state[AssignmentField.assignmentCancelTypeID] ===
            AssignmentCancelTypeEnum.CanceledNotStartedSameDay.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledNotArrived.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledArrived.toString()),
        {
          exclude: () => currentCaseType === CaseTypeEnum.Placeholder,
          field: AssignmentField.standByAssignmentIsEligible,
          key: 'Ska ha standby-tid',
          value: (
            <Column>
              <Centered>
                <Select
                  small
                  disabled={
                    !canEditField(AssignmentField.standByAssignmentIsEligible)
                  }
                  onChange={(eve) =>
                    onChange(
                      assignmentId,
                      AssignmentField.standByAssignmentIsEligible,
                      eve.currentTarget.value
                    )
                  }
                  value={state[AssignmentField.standByAssignmentIsEligible]}
                >
                  <option value="">Välj</option>
                  <option value="true">Ja</option>
                  <option value="false">Nej</option>
                </Select>
                <PaddedSpan>
                  OBS! Standby-tid räknas in i löneunderlaget.
                </PaddedSpan>
              </Centered>
            </Column>
          ),
        }
      ),
      ...arraySpreadIf(
        currentCaseType !== CaseTypeEnum.Placeholder &&
          (state[AssignmentField.assignmentCancelTypeID] ===
            AssignmentCancelTypeEnum.CanceledNotStartedSameDay.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledNotArrived.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledArrived.toString()) &&
          state[AssignmentField.standByAssignmentIsEligible] === 'true',
        {
          field: AssignmentField.standByAssignmentTimeInHours,
          key: 'Standby-tid',
          value: (
            <Column>
              <Centered>
                <RemovableInput
                  min={0}
                  max={8}
                  maxLength={4}
                  disabled={
                    !canEditField(AssignmentField.standByAssignmentTimeInHours)
                  }
                  addLabel="+ Lägg till"
                  isRemoved={
                    state[AssignmentField.standByAssignmentTimeInHours] === ''
                  }
                  onAddClick={async () => {
                    // check if another assignment (same day) has started after this one
                    const estimatedStartTime = new Date(
                      state[AssignmentField.estimatedStartTime]
                    );
                    // Standby ersätter den slot fältaren var uppbokad för, och elbilens
                    // laddningsbuffert ingår i den (samma regel som nattjobbet
                    // StandbyTimeLogic). Bufferten är serverägd och saknar formulärfält,
                    // så den läses från basmodellen precis som derivedDuration ovan.
                    const plannedSlotInHours =
                      (Number(state[AssignmentField.estimatedDuration]) +
                        (currentAssignment.electricCarBufferTime ?? 0)) /
                      hourInMilliseconds;
                    const nextStartedAssignmentTime =
                      await getNextStartedAssignmentTime();
                    // if so, use the time between the estimated start time of this assignment and the actual start time of the next one (max the planned slot of this assignment or 8 hours)
                    if (nextStartedAssignmentTime) {
                      const diffInHoursToNextAssignment =
                        (nextStartedAssignmentTime.getTime() -
                          estimatedStartTime.getTime()) /
                        hourInMilliseconds;

                      const usedTime = Math.min(
                        diffInHoursToNextAssignment,
                        plannedSlotInHours,
                        maxStandByTimeInHours
                      );

                      onChange(
                        assignmentId,
                        AssignmentField.standByAssignmentTimeInHours,
                        usedTime.toFixed(2)
                      );
                    }
                    // otherwise, use the planned slot of this assignment (max 8 hours)
                    else
                      onChange(
                        assignmentId,
                        AssignmentField.standByAssignmentTimeInHours,
                        Math.min(
                          plannedSlotInHours,
                          maxStandByTimeInHours
                        ).toString()
                      );
                  }}
                  onBlur={autoFormatNumberInputOnBlur}
                  onChange={(eve) => {
                    const rawValue = eve.currentTarget.value || '0'; // Default to 0 if user clears input
                    const newValue = Number(rawValue);

                    if (newValue > maxStandByTimeInHours) {
                      onChange(
                        assignmentId,
                        AssignmentField.standByAssignmentTimeInHours,
                        maxStandByTimeInHours.toString()
                      );
                    } else {
                      onChange(
                        assignmentId,
                        AssignmentField.standByAssignmentTimeInHours,
                        newValue.toString()
                      );
                    }
                  }}
                  onKeyDown={(eve) => {
                    // Special for chrome when the decimal point is a comma: when typing a '.',
                    // onChange value is an empty string, so we'll simply ignore all '.' keystrokes in favour of comma (',').
                    const localeUsesComma = (1.1).toLocaleString()[1] === ',';
                    if (localeUsesComma && eve.key === '.')
                      eve.preventDefault();
                  }}
                  onRemoveClick={() =>
                    onChange(
                      assignmentId,
                      AssignmentField.standByAssignmentTimeInHours,
                      ''
                    )
                  }
                  small
                  type="number"
                  value={state[AssignmentField.standByAssignmentTimeInHours]}
                />
                {state[AssignmentField.standByAssignmentTimeInHours] && (
                  <PaddedSpan>
                    {getHoursAndMinutesFromMillisecondsString(
                      Number(
                        state[AssignmentField.standByAssignmentTimeInHours]
                      ) * millisecondsInHour
                    )}{' '}
                  </PaddedSpan>
                )}
              </Centered>
            </Column>
          ),
        }
      ),
      ...arraySpreadIf(
        currentCaseType !== CaseTypeEnum.Placeholder &&
          (state[AssignmentField.assignmentCancelTypeID] ===
            AssignmentCancelTypeEnum.CanceledNotStartedSameDay.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledNotArrived.toString() ||
            state[AssignmentField.assignmentCancelTypeID] ===
              AssignmentCancelTypeEnum.CanceledArrived.toString()) &&
          state[AssignmentField.standByAssignmentIsEligible] === 'true',
        {
          key: 'Standby-tid anledning',
          value: (
            <ExtraInfoInput
              disabled={
                !canEditField(AssignmentField.standByAssignmentTimeReason)
              }
              onChange={(eve) =>
                onChange(
                  assignmentId,
                  AssignmentField.standByAssignmentTimeReason,
                  eve.currentTarget.value
                )
              }
              placeholder="Ange andledning till standby-tid (om nödvändigt)"
              value={state[AssignmentField.standByAssignmentTimeReason]}
            />
          ),
        }
      ),
      {
        exclude: () =>
          assignmentId !== -1 ||
          newAssignmentOverrides === undefined ||
          newAssignmentOverrides?.firstPossibleStartTime === undefined,
        key: 'Tidigast möjliga starttid',
        value: (
          <Input
            disabled
            small
            type="datetime-local"
            value={toInputDateTimeString(
              newAssignmentOverrides?.firstPossibleStartTime ?? new Date()
            )}
          />
        ),
      },
      {
        key: 'Estimerad starttid',
        value: (
          <>
            <Input
              error={!!validation?.[AssignmentField.estimatedStartTime]}
              disabled={
                !canEditField(AssignmentField.estimatedStartTime) ||
                (!!state[AssignmentField.startTime] &&
                  !loggedInUser?.roles.includes(
                    /* Roles.GoSupport */ Roles.Admin
                  )) // temporary allow all admins to change start time (during summer vacation)
              }
              // Min/max från först/senast möjlig-fönstret (annonsuppdrag), annars
              // min = bokningsdagens midnatt: kalenderpopupen förväljer då bokat
              // till-datumet på tomma fält, med timmar/minuter fria. Snävare
              // bookedTo-gränser gav ett fönster lika smalt som estimerad tid,
              // vilket lämnade Chromiums klickväljare utan giltiga minutval vid
              // korta uppdrag. Affärsvalideringen sköts i caseAssignmentFormsValidators.
              max={
                state[AssignmentField.lastPossibleEndTime]
                  ? toInputDateTimeString(
                      removeMilliseconds(
                        new Date(state[AssignmentField.lastPossibleEndTime]),
                        Number(state[AssignmentField.estimatedDuration]) || 0
                      )!
                    )
                  : undefined
              }
              min={
                state[AssignmentField.firstPossibleStartTime]
                  ? toInputDateTimeString(
                      new Date(state[AssignmentField.firstPossibleStartTime])
                    )
                  : state[AssignmentField.bookedTo]
                    ? toInputDateTimeString(
                        getStartOfDay(new Date(state[AssignmentField.bookedTo]))
                      )
                    : undefined
              }
              onChange={input(AssignmentField.estimatedStartTime)}
              small
              style={{
                opacity: state[AssignmentField.startTime] ? 0.4 : 1,
              }}
              type="datetime-local"
              value={state[AssignmentField.estimatedStartTime]}
            />
            {state[AssignmentField.estimatedStartTime] ? (
              !state[AssignmentField.startTime] ? (
                <>
                  {resultingEndTime && (
                    <PaddedSpan style={{ opacity: 0.4 }}>
                      Beräknad sluttid:
                      {formatDateTime(resultingEndTime)}
                    </PaddedSpan>
                  )}
                  {state[AssignmentField.addingStopOver] === 'true' &&
                    esimatedFinalEndTime && (
                      <PaddedSpan style={{ opacity: 0.4 }}>
                        inklusive mellanlandningen:
                        {formatDateTime(esimatedFinalEndTime)}
                      </PaddedSpan>
                    )}
                </>
              ) : null
            ) : (
              <PaddedSpan style={{ opacity: 0.4 }}>
                Ingen estimerad starttid
              </PaddedSpan>
            )}
          </>
        ),
      },
      mkInput(
        AssignmentField.startTime,
        'Faktisk starttid',
        'datetime-local',
        () => currentCaseType === CaseTypeEnum.Placeholder
      ),
      {
        exclude: () =>
          assignmentId !== -1 || // not a new assignment
          newAssignmentOverrides === undefined || // no new assignment overrides
          newAssignmentOverrides?.lastPossibleEndTime === undefined, // last possible end time is not set
        key: 'Senast möjlig sluttid',
        value: (
          <Input
            disabled
            small
            type="datetime-local"
            value={toInputDateTimeString(
              newAssignmentOverrides?.lastPossibleEndTime ?? new Date()
            )}
          />
        ),
      },
      {
        exclude: () => currentCaseType === CaseTypeEnum.Placeholder,
        key: 'Faktisk sluttid',
        value: (
          <>
            <Input
              disabled={!canEditField(AssignmentField.endTime)}
              min={state[AssignmentField.startTime]}
              onChange={input(AssignmentField.endTime)}
              small
              type="datetime-local"
              value={state[AssignmentField.endTime]}
            />
            {state[AssignmentField.estimatedDuration] &&
              state[AssignmentField.startTime] &&
              !state[AssignmentField.endTime] && (
                <PaddedSpan style={{ opacity: 0.4 }}>
                  Beräknad sluttid:{' '}
                  {resultingEndTime ? formatDateTime(resultingEndTime) : ''}
                  {/* <Input
                          disabled
                          small
                          type="datetime-local"
                          value={
                            resultingEndTime ? formatDateTime(resultingEndTime) : '–'
                          }
                        /> */}
                </PaddedSpan>
              )}
          </>
        ),
      },
      {
        key: 'Övernattning',
        value: (
          <>
            <Horizontal style={{ alignItems: 'center', gap: 8 }}>
              <Select
                disabled={!canEditField(AssignmentField.stayOverTypeID)}
                onChange={(eve) => {
                  input(AssignmentField.stayOverTypeID)(eve);
                }}
                small
                value={state[AssignmentField.stayOverTypeID]}
                width="auto"
              >
                {stayoverTypeOptions.map(({ type, label }) => (
                  <option
                    disabled={
                      hotelOptionDisabled && type === StayOverTypeEnum.HOTEL
                    }
                    key={`${type}-${label}`}
                    value={type}
                  >
                    {label}
                  </option>
                ))}
              </Select>
              <WeeklyHoursStayOverWarning
                stayOverTypeIdSelected={isStayOverTypeSelected(
                  state[AssignmentField.stayOverTypeID]
                )}
                report={drivingPolicyReportResult.data}
              />
              {/* Hotell/Privat ger nattraktamente (löneart 845) i Hogia-exporten, Lastbil gör det inte */}
              {isStayOverTypeSelected(state[AssignmentField.stayOverTypeID]) &&
                Number(state[AssignmentField.stayOverTypeID]) !==
                  StayOverTypeEnum.TRUCK && (
                  <PaddedSpan style={{ fontSize: '0.85em', color: '#666' }}>
                    Medför nattraktamente på FT:s lön
                  </PaddedSpan>
                )}
            </Horizontal>
            {hotelOptionDisabled && (
              <WarningDiv style={{ fontSize: '0.85em' }}>
                <FontAwesomeIcon icon={faWarning} /> {hotelNightConflictMessage}
              </WarningDiv>
            )}
          </>
        ),
      },
      {
        exclude: () => currentAssignment.stayOverTypeID === undefined,
        key: 'Hotell-/övernattningsinfo',
        keyAlign: 'top',
        value: (
          <ExtraInfoInput
            disabled={!canEdit}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.stayOverInfo,
                eve.currentTarget.value
              )
            }
            value={state[AssignmentField.stayOverInfo]}
          />
        ),
      },
      {
        exclude: () =>
          (currentCaseType === CaseTypeEnum.Placeholder &&
            state[AssignmentField.fixedAssignmentTimeInHours] === '' &&
            !isCalculatingFixedTime) ||
          // Låst fält (persontransport, samåkning, eller elbil för andra än Efterkontroll/SuperAdmin): raden
          // döljs när den är tom. Ett liggande värde (från Efterkontroll/SuperAdmin, eller ett
          // godkänt uppdrag från innan regeln) visas fortfarande, men låst. Däckkörning visar
          // raden ändå, med en förklaring i stället för fältet.
          (fixedTimeLocked &&
            !neverFixedTime &&
            state[AssignmentField.fixedAssignmentTimeInHours] === ''),
        key: 'Fast uppdragstid (h)',
        value: (
          <Horizontal style={{ alignItems: 'center', gap: 6 }}>
            {neverFixedTime &&
            state[AssignmentField.fixedAssignmentTimeInHours] === '' ? (
              <div style={{ fontSize: '0.8em', color: '#666', maxWidth: 320 }}>
                Däckkörning har ingen fast tid – ange estimerad uppdragstid.
              </div>
            ) : (
              <RemovableInput
                disabled={
                  !canEditField(AssignmentField.fixedAssignmentTimeInHours) ||
                  fixedTimeLocked
                }
                addLabel="+ Lägg till"
                isRemoved={
                  state[AssignmentField.fixedAssignmentTimeInHours] === ''
                }
                onAddClick={() => {
                  if (canEdit) {
                    onChange(
                      assignmentId,
                      AssignmentField.fixedAssignmentTimeInHours,
                      '0'
                    );
                  }
                }}
                onBlur={autoFormatNumberInputOnBlur}
                onChange={(eve) =>
                  onChange(
                    assignmentId,
                    AssignmentField.fixedAssignmentTimeInHours,
                    eve.currentTarget.value || '0' // Default to 0 if user clears input with backspace
                  )
                }
                onKeyDown={(eve) => {
                  // Special for chrome when the decimal point is a comma: when typing a '.',
                  // onChange value is an empty string, so we'll simply ignore all '.' keystrokes in favour of comma (',').
                  const localeUsesComma = (1.1).toLocaleString()[1] === ',';
                  if (localeUsesComma && eve.key === '.') eve.preventDefault();
                }}
                onRemoveClick={() =>
                  onChange(
                    assignmentId,
                    AssignmentField.fixedAssignmentTimeInHours,
                    ''
                  )
                }
                small
                type="number"
                value={state[AssignmentField.fixedAssignmentTimeInHours]}
              />
            )}
            {neverFixedTime &&
              state[AssignmentField.fixedAssignmentTimeInHours] !== '' && (
                <div
                  style={{ fontSize: '0.8em', color: '#666', maxWidth: 320 }}
                >
                  Däckkörning har ingen fast tid – värdet ligger kvar på det
                  godkända uppdraget men kan inte ändras.
                </div>
              )}
            {isCalculatingFixedTime && (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                title="Räknar ut fast tid…"
              />
            )}
            {(isElectricCase || isSamakning) && !isPersonalTransport && (
              <div style={{ fontSize: '0.8em', color: '#666', maxWidth: 320 }}>
                {canSetFixedTimeByHand
                  ? `${isSamakning ? 'Samåkningar' : 'Elbilsärenden'} får ingen fast tid från prislistan – du kan lägga in den för hand.`
                  : `Fast tid på ${isSamakning ? 'samåkningar' : 'elbilsärenden'} sätts bara av Efterkontroll eller SuperAdmin.`}
              </div>
            )}
          </Horizontal>
        ),
      },
      {
        key: 'Matrast (min)',
        value: (
          <Horizontal style={{ alignItems: 'center', gap: 8 }}>
            <Input
              onChange={input(AssignmentField.breakTime)}
              small
              type="number"
              value={state[AssignmentField.breakTime]}
              disabled={!canEditField(AssignmentField.breakTime)}
            />
            {Number(state[AssignmentField.fixedAssignmentTimeInHours]) > 0 && (
              <div
                style={{
                  fontSize: '0.8em',
                  color: '#666',
                  maxWidth: '320px',
                }}
              >
                Rasten ligger inom den totala estimerade tiden (fast tid +
                rast). Fast tid är den betalda arbetstiden och påverkas inte av
                rastens längd.
              </div>
            )}
          </Horizontal>
        ),
      },
      {
        exclude: () =>
          currentCaseType === CaseTypeEnum.Placeholder ||
          currentCaseType === CaseTypeEnum.CarTransportTruck,
        key: 'Information från Ride',
        keyAlign: 'top',
        value: (
          <ExtraInfoInput
            disabled={!canEdit}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.extraInfo,
                eve.currentTarget.value
              )
            }
            value={state[AssignmentField.extraInfo]}
          />
        ),
      },
      {
        exclude: () => currentCaseType === CaseTypeEnum.CarTransportTruck,
        key: 'Förväntade utgifter',
        value: (
          <MultiSelect
            disabled={!canEditField(AssignmentField.expectedExpenses)}
            onChange={(v) => {
              onChange(
                assignmentId,
                AssignmentField.expectedExpenses,
                Array.from(v).join(',')
              );
            }}
            options={expenseTypeOptions}
            small
            value={
              new Set(
                !state[AssignmentField.expectedExpenses]
                  ? []
                  : state[AssignmentField.expectedExpenses].split(',')
              )
            }
          />
        ),
      },
      {
        exclude: () =>
          !hasAccessToInvoiceExtras ||
          currentCaseType === CaseTypeEnum.CarTransportTruck,
        key: 'Fakturera extra',
        value: (
          <InvoiceExtraDropdown
            assignmentId={assignmentId ?? -1}
            invoiceExtras={currentAssignment.invoiceExtras ?? []}
            readOnly={!canEditInvoiceExtras}
            onDataChanged={() => caseResponse.update()}
          />
        ),
      },
      {
        // Samma roller som efterkontrollvyn (endpointen är också låst till dem).
        exclude: () =>
          !canEditInvoiceExtras ||
          !assignmentId ||
          assignmentId <= 0 ||
          currentCaseType === CaseTypeEnum.Placeholder,
        key: 'Autogodkännande',
        value: (
          <AutoApproveCheckDropdown
            caseId={currentAssignment.caseID}
            assignmentId={assignmentId ?? -1}
            assignmentStatusID={currentAssignment.assignmentStatusID}
            autoApprove={currentAssignment.autoApprove}
            autoApprovedAt={currentAssignment.autoApprovedAt}
          />
        ),
      },
      {
        exclude: () =>
          !isSelfTransportationAssignment(currentAssignment.assignmentTypeID),
        key: 'Fälttestaren bokar egen biljett',
        value: (
          <Checkbox
            disabled={
              !canEditField(AssignmentField.assigneeShouldBookTheirOwnTicket)
            }
            checked={
              state[AssignmentField.assigneeShouldBookTheirOwnTicket] === 'true'
            }
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.assigneeShouldBookTheirOwnTicket,
                eve.currentTarget.checked ? 'true' : 'false'
              )
            }
          />
        ),
      },
      {
        exclude: () =>
          !isSelfTransportationAssignment(currentAssignment.assignmentTypeID),
        key: 'Biljett-information',
        value: (
          <ExtraInfoInput
            disabled={!canEditField(AssignmentField.ticketInfo)}
            onChange={(eve) =>
              onChange(
                assignmentId,
                AssignmentField.ticketInfo,
                eve.currentTarget.value
              )
            }
            value={state[AssignmentField.ticketInfo]}
          />
        ),
      },
      {
        exclude: () =>
          currentCaseType === CaseTypeEnum.CarTransportTruck ||
          !canEditField(AssignmentField.standardizedComments), // hide this entirely if not editable
        key: 'Information till fälttestare',
        value: (
          <StandardizedComments
            disabled={!canEditField(AssignmentField.standardizedComments)}
            assignmentId={assignmentId ?? -1}
            commentsOnAssignment={parseStandardizedComments(
              state[AssignmentField.standardizedComments]
            )}
            onChange={(newComments) => {
              // first, update any linked expense type information
              const existingComments = parseStandardizedComments(
                state[AssignmentField.standardizedComments]
              );
              const expectedExpenses = JSON.parse(
                `[${state[AssignmentField.expectedExpenses] || ''}]`
              ) as ExpenseType[];

              const uniqueExpectedExpenses = getUpdatedExpectedExpensesList({
                existingComments,
                existingExpectedExpenses: expectedExpenses,
                newComments,
                globalStandardizedComments,
              });

              // update the expected expenses state
              onChange(
                assignmentId,
                AssignmentField.expectedExpenses,
                uniqueExpectedExpenses.join(',')
              );

              // then update the standardized comments state
              onChange(
                assignmentId,
                AssignmentField.standardizedComments,
                JSON.stringify(newComments ?? [])
              );
            }}
          />
        ),
      },
    ];
    return result.filter((r) => !r.exclude?.());
  }, [
    addStopOverRow,
    neverFixedTime,
    fieldTesterPlanningWindow,
    followsCarrier,
    requiresCarrier,
    addresses,
    assignmentId,
    assignmentStatuses,
    canEditInvoiceExtras,
    hasAccessToInvoiceExtras,
    caseResponse,
    assignmentTypeOptions,
    assignmentTypes,
    bookedToChangeReason,
    bookedToChanged,
    canEdit,
    canEditField,
    cancelTypes,
    caseEvents,
    caseState,
    currentAssignment,
    currentCaseType,
    drivingPolicyReportResult.data,
    esimatedFinalEndTime,
    estimatedWorkTimeMillis,
    expenseTypeOptions,
    getNextStartedAssignmentTime,
    globalStandardizedComments,
    hotelNightConflictMessage,
    hotelOptionDisabled,
    input,
    isAssignedToCarTransport,
    isCalculatingFixedTime,
    isElectricCase,
    canSetFixedTimeByHand,
    fixedTimeLocked,
    isPersonalTransport,
    isSamakning,
    isViolatingDrivingPolicy,
    loadingStatuses,
    loggedInUser?.roles,
    mkAddressInput,
    mkInput,
    multiplePurchasesList,
    newAssignmentOverrides,
    onChange,
    onOnHoldClick,
    personalTransportFromAssignmentRow,
    personalTransportToAssignmentRow,
    resultingEndTime,
    state,
    stayoverTypeOptions,
    stopOverToEnd,
    unmodifiedAssignment,
    validation,
    zipCodeAreas,
  ]);

  return rows;
};

export default useCaseAssignmentFormRows;
